// src/components/auth/PublicAuthControl.tsx
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { HomeLink } from "@/components/HomeLink";
import { ClaimYourProfileButton } from "@/components/auth/ClaimYourProfileButton";
import { writeOnboarding } from "@/lib/onboarding-context";

/**
 * `tone="onBrand"` is for a club-coloured header band: an ice outline instead of the emerald fill,
 * so one button does not shout over the artwork it sits on. Every other page keeps the default.
 */
export const PublicAuthControl = ({
  communitySlug,
  tone = "default",
}: {
  communitySlug?: string;
  tone?: "default" | "onBrand";
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onBrand = tone === "onBrand";
  if (user) {
    return (
      <span className={`inline-flex items-center gap-1.5 ${onBrand ? "text-sanur-ice" : ""}`}>
        <HomeLink />
      </span>
    );
  }
  if (communitySlug) return <ClaimYourProfileButton communitySlug={communitySlug} />;
  return (
    // Signing in from a public page means coming back to it. Writing a context with no returnTo
    // also OVERWRITES one a door has already set: someone who opened a ladder invite link and
    // then used this button rather than the one in the card lost the invite entirely and landed
    // on their home page, with no way back to the registration they had been sent.
    <Button
      size="sm"
      variant={onBrand ? "outline" : "default"}
      className={`h-7 px-2.5 text-xs ${onBrand ? "border-sanur-ice/40 bg-transparent text-sanur-ice hover:bg-sanur-ice/10 hover:text-sanur-ice" : ""}`}
      onClick={() => {
        writeOnboarding({ door: "home", returnTo: location.pathname + location.search });
        navigate("/login");
      }}
    >
      Sign in
    </Button>
  );
};
